import { SortState } from '@shared/components/data-table';
import {
  PageInfo,
  useGetUsersLazyQuery,
  useGetUsersQuery,
} from '@shared/generated/graphql';
import { PaginationState } from '@shared/hooks/usePagination';
import { toName, User } from '@shared/types/user';

type UseGetUsersOptions = {
  organizationIds?: string[];
  searchTerm?: string;
  pollIntervalMs?: number;
  emails?: string[];
  skip?: boolean;
  sort?: SortState;
  pagination?: PaginationState;
};

type UseGetUsersState = {
  users: User[];
  pageInfo?: PageInfo;
  isLoading: boolean;
  count: number;
  totalCount: number;
};

type UseGetUsersActions = {
  // add any actions the hook provides
};

export type UseGetUsersValue = UseGetUsersState & UseGetUsersActions;

export const useGetUsers = (props?: UseGetUsersOptions): UseGetUsersValue => {
  const { data, loading } = useGetUsersQuery({
    variables: {
      input: {
        organizationIds: props?.organizationIds,
        searchTerm: props?.searchTerm,
        emails: props?.emails,
      },
      sort: props?.sort,
      ...props?.pagination,
    },
    skip: props?.skip,
    ...(props?.pollIntervalMs && { pollInterval: props.pollIntervalMs }),
  });

  if (!data)
    return {
      users: [],
      isLoading: loading,
      pageInfo: {
        hasNextPage: false,
        hasPreviousPage: false,
      },
      count: 0,
      totalCount: 0,
    };

  const users = data.users.edges.map((user) => ({
    id: user.node.id,
    userName: user.node.username ?? '',
    name: toName(user.node),
    email: user.node.email ?? undefined,
    roles: user.node.roles,
    lastSignedIn: user.node?.lastSignedIn,
  }));

  return {
    users: users,
    isLoading: loading,
    pageInfo: data?.users.pageInfo,
    totalCount: data?.users.totalCount || 0,
    count: data?.users.count || 0,
  };
};

type UseFetchUsersOptions = {
  organizationIds: string[];
};

export const useFetchUsers = (p: UseFetchUsersOptions) => {
  const [getUsers] = useGetUsersLazyQuery();

  const fetchUsers = async (searchTerm: string) => {
    const { data } = await getUsers({
      variables: {
        input: {
          organizationIds: p.organizationIds,
          searchTerm: searchTerm,
        },
      },
    });
    if (!data) return [];
    return data.users.edges.map((user) => ({
      id: user.node.id,
      userName: user.node.username ?? '',
      name: toName(user.node),
      email: user.node.email ?? undefined,
      roles: user.node.roles,
    }));
  };

  return { fetchUsers };
};
